/**
 * Design-QA route for the Awards screen (mirrors SkillTreeReview).
 * Mounts <AwardsView> with a fabricated mid-journey snapshot so every
 * visual state is on screen at once — unlocked badges in each category
 * tone, locked badges with partial progress, locked badges at zero, the
 * next-up chip, and the hero counter. No Supabase session needed.
 *
 * `?state=fresh` shows the brand-new-account variant (nothing unlocked).
 */
import { useSearchParams } from 'react-router';
import { Screen } from '@fluent/ui';
import { AwardsView } from './screens/Achievements.js';
import { deriveTimeStats, type ProgressSnapshot } from './achievements/definitions.js';
import { getCourseTree } from './content/course.js';
import type { CompletionRow } from './progress/hooks.js';

function buildMidSnapshot(): ProgressSnapshot {
  const tree = getCourseTree();
  // Complete the first skill fully (skill badge) + enough extra lessons to
  // pass 5 total, with perfect runs, an early-morning finish, a late-night
  // finish, and a Sat+Sun pair sprinkled in.
  const lessonIds = tree ? tree.orderedLessonIds.slice(0, 9) : [];
  const stamps = [
    '2026-07-04T07:10:00', // Saturday, early bird
    '2026-07-05T12:00:00', // Sunday — completes the weekend pair
    '2026-07-06T22:40:00', // night owl
    '2026-07-07T10:00:00',
    '2026-07-07T11:00:00',
    '2026-07-07T12:00:00',
    '2026-07-08T09:00:00',
    '2026-07-09T09:00:00',
    '2026-07-10T09:00:00',
  ];
  const completions: Record<string, CompletionRow> = {};
  lessonIds.forEach((id, i) => {
    completions[id] = {
      lesson_id: id,
      xp_earned: 20,
      accuracy: i % 3 === 0 ? 1 : 0.9,
      perfect: i % 3 === 0,
      completed_at: stamps[i] ?? '2026-07-10T09:00:00',
    };
  });
  return {
    completions,
    streak: { current_streak: 4, longest_streak: 8, freezes_available: 2, last_active_date: null },
    totalXp: 1250,
    vocabCount: 62,
    hasProAccess: true,
    skillsCompleted: 1,
    unitsCompleted: 0,
    time: deriveTimeStats(completions),
  };
}

const FRESH: ProgressSnapshot = {
  completions: {},
  streak: undefined,
  totalXp: 0,
  vocabCount: 0,
  hasProAccess: false,
  skillsCompleted: 0,
  unitsCompleted: 0,
  time: deriveTimeStats({}),
};

export function AwardsReview() {
  const [params] = useSearchParams();
  const snapshot = params.get('state') === 'fresh' ? FRESH : buildMidSnapshot();
  return (
    <Screen>
      <AwardsView snapshot={snapshot} />
    </Screen>
  );
}
